import { computed, getCurrentInstance, onUnmounted, ref, type Ref } from 'vue'
import axios, { type CancelTokenSource } from 'axios'
import ExceptionService from '@/Network/Services/DoctorStationException'
import type {
  SimulateExceptionPayload,
  SimulateFinancialsResult
} from '@/Network/Types/Requests/DoctorStationExceptionRequests'
import { useFeatureFlags, type ExceptionSimulateMode } from '@/hooks/useFeatureFlags'
import {
  calculeteBaseCost,
  calculateExceptionalCost,
  calculatePredictedResult,
  calculeteTotalAgreedValue
} from '@/utils/math'

export const SIMULATE_DEBOUNCE_MS = 300

export interface LocalCalculationInput {
  baseDuration: number
  exceptionalDuration: number
  baseValue: number
  agreedHourlyRate: number
  tax: number
}

export type DivergenceReporter = (
  local: SimulateFinancialsResult,
  remote: SimulateFinancialsResult
) => void

/**
 * Calcula os financials localmente reusando EXATAMENTE as mesmas funções e a
 * mesma ordem de argumentos que AgreedValue.vue/NewExceptionalityRequest.vue
 * já usam hoje — garante paridade byte-a-byte com o comportamento atual
 * quando a flag está 'off'. NÃO alterar esta função sem revalidar os
 * componentes consumidores (math.ts não pode ser deletado nesta fase).
 */
export function computeLocalFinancials(input: LocalCalculationInput): SimulateFinancialsResult {
  const { baseDuration, exceptionalDuration, baseValue, agreedHourlyRate, tax } = input

  const baseCost = calculeteBaseCost(baseDuration, exceptionalDuration, baseValue)
  const exceptionalCost = calculateExceptionalCost(
    exceptionalDuration,
    baseDuration,
    agreedHourlyRate,
    tax
  )
  const totalExceptionalValue = calculeteTotalAgreedValue(agreedHourlyRate, exceptionalDuration)
  const estimatedLoss = calculatePredictedResult(exceptionalCost, baseCost, exceptionalDuration)
  const lostHours = Math.max(baseDuration - exceptionalDuration, 0)

  return {
    baseDurationHours: baseDuration.toFixed(2),
    exceptionalDurationHours: exceptionalDuration.toFixed(2),
    lostHours: lostHours.toFixed(2),
    valueOfLostHours: (lostHours * baseValue).toFixed(2),
    baseCost: baseCost.toFixed(2),
    exceptionalCost: exceptionalCost.toFixed(2),
    estimatedLoss: estimatedLoss.toFixed(2),
    estimatedLossTotal: estimatedLoss.toFixed(2),
    totalExceptionalValue: totalExceptionalValue.toFixed(2)
  }
}

const MONEY_FIELDS: (keyof SimulateFinancialsResult)[] = [
  'baseCost',
  'exceptionalCost',
  'estimatedLoss',
  'estimatedLossTotal',
  'totalExceptionalValue',
  'valueOfLostHours'
]

export function numbersDiverge(
  a: string | null | undefined,
  b: string | null | undefined,
  toleranceCents = 1
): boolean {
  const aMissing = a === null || a === undefined
  const bMissing = b === null || b === undefined

  // Se um está faltando e o outro não, divergem
  if (aMissing !== bMissing) return true

  // Se ambos estão faltando, não divergem
  if (aMissing && bMissing) return false

  // Ambos presentes, compara numericamente
  const diffInCents = Math.abs(Number(a) - Number(b)) * 100
  return diffInCents >= toleranceCents
}

export function financialsDiverge(
  local: SimulateFinancialsResult,
  remote: SimulateFinancialsResult
): boolean {
  return MONEY_FIELDS.some((field) => numbersDiverge(local[field], remote[field]))
}

const defaultDivergenceReporter: DivergenceReporter = (local, remote) => {
  // eslint-disable-next-line no-console
  console.warn('[useExceptionSimulation] divergência local vs simulate detectada', {
    local,
    remote
  })
}

export interface UseExceptionSimulationOptions {
  debounceMs?: number
  onDivergence?: DivergenceReporter
  exceptionService?: Pick<ExceptionService, 'simulate'>
}

export function useExceptionSimulation(options: UseExceptionSimulationOptions = {}) {
  const { flags } = useFeatureFlags()
  const debounceMs = options.debounceMs ?? SIMULATE_DEBOUNCE_MS
  const onDivergence = options.onDivergence ?? defaultDivergenceReporter
  const exceptionService = options.exceptionService ?? new ExceptionService()

  const loading: Ref<boolean> = ref(false)
  const error: Ref<string | null> = ref(null)
  const data: Ref<SimulateFinancialsResult | null> = ref(null)

  let debounceTimer: ReturnType<typeof setTimeout> | undefined
  let cancelSource: CancelTokenSource | undefined
  let requestSeq = 0

  function clearPending() {
    if (debounceTimer !== undefined) {
      clearTimeout(debounceTimer)
      debounceTimer = undefined
    }
    if (cancelSource) {
      cancelSource.cancel('superseded-by-new-simulation')
      cancelSource = undefined
    }
  }

  function simulate(
    doctorStationId: number | null | undefined,
    payload: SimulateExceptionPayload,
    localInput: LocalCalculationInput
  ) {
    clearPending()

    const mode: ExceptionSimulateMode = flags.exceptionSimulateMode

    if (mode === 'off') {
      data.value = computeLocalFinancials(localInput)
      loading.value = false
      error.value = null
      return
    }

    if (mode === 'shadow') {
      // shadow SEMPRE exibe o valor local — a comparação com o remoto
      // acontece de forma invisível quando a resposta chega (ver Task 6).
      data.value = computeLocalFinancials(localInput)
    }

    if (!doctorStationId) {
      return
    }

    loading.value = true
    error.value = null

    const mySeq = ++requestSeq

    debounceTimer = setTimeout(() => {
      cancelSource = axios.CancelToken.source()
      const myCancelSource = cancelSource

      exceptionService
        .simulate(doctorStationId, payload, myCancelSource.token)
        .then((response) => {
          if (mySeq !== requestSeq) return // resposta obsoleta (stale) — ignora

          loading.value = false

          if (mode === 'shadow') {
            const local = computeLocalFinancials(localInput)
            if (financialsDiverge(local, response.financials)) {
              onDivergence(local, response.financials)
            }
            // shadow: NUNCA sobrescreve data.value com o remoto — permanece local.
          } else {
            data.value = response.financials
          }
        })
        .catch((err) => {
          if (mySeq !== requestSeq) return
          if (axios.isCancel(err)) return

          loading.value = false

          if (mode === 'shadow') {
            // shadow: falha da API nunca vira erro visível na UI — o valor
            // local já exibido permanece, e a falha só é logada.
            // eslint-disable-next-line no-console
            console.warn('[useExceptionSimulation] falha ao chamar simulate em modo shadow', err)
            return
          }

          error.value = 'Não foi possível calcular os valores da excepcionalidade.'

          if (mode === 'on') {
            data.value = null
          }
        })
    }, debounceMs)
  }

  const canSubmit = computed(() => {
    if (flags.exceptionSimulateMode !== 'on') return true
    return !loading.value && !error.value && data.value !== null
  })

  if (getCurrentInstance()) {
    onUnmounted(clearPending)
  }

  return { loading, error, data, simulate, canSubmit, cancel: clearPending }
}
